import { apiClient } from "@api/client";
import { request } from "@api/request";
import type { components } from "@api/schema";
import { fetchClient } from "@patients/clientsClient";
import type { SpriteTraits } from "@scribenote/brian-design-system";

type PatientOut = components["schemas"]["PatientOut"];

/** The patient columns brian records under ADR-69 (`models/patient.py`). */
export interface PatientSignalment {
  species: PatientOut["species"];
  breed: string | null;
  sex: PatientOut["sex"];
  reproductiveStatus: PatientOut["reproductive_status"];
  birthDate: string | null;
  birthDatePrecision: PatientOut["birth_date_precision"];
  color: string | null;
  microchip: string | null;
  deceasedAt: string | null;
}

export interface Patient {
  id: string;
  name: string;
  householdId: string;
  /** The household member brian reaches for first; null for a household with no people. */
  primaryClientId: string | null;
  signalment: PatientSignalment;
  sprite: SpriteTraits;
}

export const toPatientSignalment = (p: PatientOut): PatientSignalment => ({
  species: p.species,
  breed: p.breed,
  sex: p.sex,
  reproductiveStatus: p.reproductive_status,
  birthDate: p.birth_date,
  birthDatePrecision: p.birth_date_precision,
  color: p.color,
  microchip: p.microchip,
  deceasedAt: p.deceased_at,
});

export const toPatient = (p: PatientOut): Patient => ({
  id: p.uuid,
  name: p.name,
  householdId: p.household_uuid,
  primaryClientId: p.primary_client_uuid,
  signalment: toPatientSignalment(p),
  sprite: p.sprite,
});

export const patientsClient = {
  get: (id: string): Promise<Patient> =>
    request(() =>
      apiClient.GET("/patients/{patient_uuid}", {
        params: { path: { patient_uuid: id } },
      }),
    ).then(toPatient),

  listForHousehold: (householdId: string): Promise<Patient[]> =>
    request(() =>
      apiClient.GET("/patients", {
        params: { query: { household_uuid: householdId } },
      }),
    ).then((rows) => rows.map(toPatient)),

  /** The pets of the client's household, not only that person's own. */
  listForClient: (clientId: string): Promise<Patient[]> =>
    fetchClient(clientId).then((client) =>
      patientsClient.listForHousehold(client.householdId),
    ),
};
